/**
 * AI Workspace Sync Service
 *
 * Provides utility functions for syncing execution info to AI workspace.
 */

import { useTerminalStore } from '../store/terminalStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { isQueryTab } from '../../workspace/core/types/workspaceTypes';
import { useAppStore } from '../../../store/appStore';
import type { LastExecutionInfo, HistoryColumn } from '../../../types/aiWorkspace';
import { safeConnectionServer } from '../../../types/safeConnectionDisplay';

/**
 * Execution result with optional result data for history
 */
type ExecutionResultData = {
  success: boolean;
  duration: number;
  rowCount: number;
  error?: string;
  query?: string;
  columns?: HistoryColumn[];
  rows?: Record<string, unknown>[];
};

/**
 * Sync query execution info to all AI workspace sessions.
 * Call this after a query completes execution.
 */
export function syncQueryExecution(
  tabId: string,
  tabName: string,
  executionResult: ExecutionResultData
): void {
  if (!window.electronAPI?.aiWorkspace) return;

  const aiTerminalSettings = useAppStore.getState().aiTerminalSettings;

  // Get AI terminal sessions
  const sessions = useTerminalStore.getState().sessions;
  const aiSessions = sessions.filter((s) => s.type === 'ai');

  if (aiSessions.length === 0) return;

  const executedAt = new Date();

  // Build execution info
  const lastExecution: LastExecutionInfo = {
    tabId,
    tabName,
    time: executedAt.toISOString(),
    duration: executionResult.duration / 1000, // Convert ms to seconds
    rowCount: executionResult.rowCount,
    status: executionResult.success ? 'success' : 'error',
    error: executionResult.error,
  };

  // Get tabs info for context
  const tabs = useWorkspaceStore.getState().tabs;
  const activeTabId = useWorkspaceStore.getState().activeTabId;
  const tabsInfo = tabs
    .filter(isQueryTab)
    .map((tab) => ({
      tabId: tab.id,
      tabName: tab.title,
      isActive: tab.id === activeTabId,
    }));

  // Sync to all AI sessions
  for (const session of aiSessions) {
    // Get connection info for this session
    const activeConnections = useConnectionStore.getState().activeConnections;
    const connection = activeConnections.find((c) => c.id === session.connectionId);

    if (!connection) continue;

    const connectionContext = {
      engine: (connection.connection.engine || 'mssql') as 'mssql' | 'pgsql',
      server: safeConnectionServer(connection.connection),
      database: session.database,
    };

    if (aiTerminalSettings.agentContext.enabled) {
      // Sync context files (CLAUDE.md)
      window.electronAPI.aiWorkspace
        .syncContext(session.id, connectionContext, tabsInfo, lastExecution)
        .catch((err) => {
          console.error(`[AiWorkspaceSyncService] Failed to sync execution to session ${session.id}:`, err);
        });
    }

    // Save to history if query is provided
    if (aiTerminalSettings.history.enabled && executionResult.query) {
      window.electronAPI.aiWorkspace
        .saveHistory({
          sessionId: session.id,
          tabId,
          tabName,
          query: executionResult.query,
          executedAt: executedAt.toISOString(),
          duration: executionResult.duration / 1000,
          rowCount: executionResult.rowCount,
          status: executionResult.success ? 'success' : 'error',
          columns: executionResult.columns,
          rows: executionResult.rows,
          error: executionResult.error,
        })
        .catch((err) => {
          console.error(`[AiWorkspaceSyncService] Failed to save history to session ${session.id}:`, err);
        });
    }
  }
}
